/**
 * Mock Session Data
 * Sample sessions for development and testing
 */

import type { UserSession, SessionStatus, DeviceType } from '@/types/session.types'
import { mediumOwner, mediumAdmin, mediumPartner1, soloOwner } from './mockUsers'

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a random session ID
 */
function generateSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Generate a mock session token
 */
function generateSessionToken(userId: string): string {
  return `token-${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 12)}`
}

/**
 * Get expiry date (24 hours from now)
 */
function getExpiryDate(): string {
  const date = new Date()
  date.setHours(date.getHours() + 24)
  return date.toISOString()
}

/**
 * Get past date
 */
function getPastDate(daysAgo: number): string {
  const date = new Date()
  date.setDate(date.getDate() - daysAgo)
  return date.toISOString()
}

// ============================================================================
// MOCK SESSIONS
// ============================================================================

export const mockSessions: UserSession[] = [
  // Current session for medium owner (desktop - Chrome)
  {
    sessionId: 'session-current-001',
    userId: mediumOwner.userId,
    sessionToken: generateSessionToken(mediumOwner.userId),
    ipAddress: '103.21.125.45',
    userAgent:
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
    deviceType: 'desktop',
    deviceName: 'Windows PC',
    browser: 'Chrome',
    browserVersion: '120',
    os: 'Windows',
    osVersion: '10',
    country: 'India',
    countryCode: 'IN',
    city: 'Bangalore',
    region: 'Karnataka',
    status: 'active',
    isCurrentSession: true,
    createdAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
    expiresAt: getExpiryDate(),
  },

  // Mobile session for medium owner
  {
    sessionId: 'session-mobile-001',
    userId: mediumOwner.userId,
    sessionToken: generateSessionToken(mediumOwner.userId),
    ipAddress: '49.207.52.78',
    userAgent:
      'Mozilla/5.0 (iPhone; CPU iPhone OS 17_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.1 Mobile/15E148 Safari/604.1',
    deviceType: 'mobile',
    deviceName: 'iPhone',
    browser: 'Safari',
    browserVersion: '17.1',
    os: 'iOS',
    osVersion: '17.1',
    country: 'India',
    countryCode: 'IN',
    city: 'Bangalore',
    region: 'Karnataka',
    status: 'active',
    isCurrentSession: false,
    createdAt: getPastDate(2),
    lastActivityAt: getPastDate(0),
    expiresAt: getExpiryDate(),
  },

  // Admin session
  {
    sessionId: 'session-admin-001',
    userId: mediumAdmin.userId,
    sessionToken: generateSessionToken(mediumAdmin.userId),
    ipAddress: '122.166.89.112',
    userAgent:
      'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
    deviceType: 'desktop',
    deviceName: 'MacBook Pro',
    browser: 'Chrome',
    browserVersion: '120',
    os: 'macOS',
    osVersion: '10.15.7',
    country: 'India',
    countryCode: 'IN',
    city: 'Bangalore',
    region: 'Karnataka',
    status: 'active',
    isCurrentSession: false,
    createdAt: getPastDate(1),
    lastActivityAt: new Date().toISOString(),
    expiresAt: getExpiryDate(),
  },

  // Partner session
  {
    sessionId: 'session-partner-001',
    userId: mediumPartner1.userId,
    sessionToken: generateSessionToken(mediumPartner1.userId),
    ipAddress: '157.45.123.89',
    userAgent:
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:121.0) Gecko/20100101 Firefox/121.0',
    deviceType: 'desktop',
    deviceName: 'Windows PC',
    browser: 'Firefox',
    browserVersion: '121',
    os: 'Windows',
    osVersion: '10',
    country: 'India',
    countryCode: 'IN',
    city: 'Mumbai',
    region: 'Maharashtra',
    status: 'active',
    isCurrentSession: false,
    createdAt: getPastDate(3),
    lastActivityAt: getPastDate(0),
    expiresAt: getExpiryDate(),
  },

  // Expired session (owner)
  {
    sessionId: 'session-expired-001',
    userId: mediumOwner.userId,
    sessionToken: generateSessionToken(mediumOwner.userId),
    ipAddress: '103.21.125.45',
    userAgent:
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/119.0.0.0 Safari/537.36',
    deviceType: 'desktop',
    deviceName: 'Windows PC',
    browser: 'Chrome',
    browserVersion: '119',
    os: 'Windows',
    osVersion: '10',
    country: 'India',
    countryCode: 'IN',
    city: 'Bangalore',
    region: 'Karnataka',
    status: 'expired',
    isCurrentSession: false,
    createdAt: getPastDate(10),
    lastActivityAt: getPastDate(9),
    expiresAt: getPastDate(9),
  },

  // Revoked session (admin)
  {
    sessionId: 'session-revoked-001',
    userId: mediumAdmin.userId,
    sessionToken: generateSessionToken(mediumAdmin.userId),
    ipAddress: '49.36.78.90',
    userAgent:
      'Mozilla/5.0 (Linux; Android 13; SM-G998B) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Mobile Safari/537.36',
    deviceType: 'mobile',
    deviceName: 'Samsung Galaxy S21',
    browser: 'Chrome',
    browserVersion: '120',
    os: 'Android',
    osVersion: '13',
    country: 'India',
    countryCode: 'IN',
    city: 'Delhi',
    region: 'Delhi',
    status: 'revoked',
    isCurrentSession: false,
    createdAt: getPastDate(5),
    lastActivityAt: getPastDate(4),
    expiresAt: getPastDate(4),
    loggedOutAt: getPastDate(4),
  },

  // Solo owner session
  {
    sessionId: 'session-solo-001',
    userId: soloOwner.userId,
    sessionToken: generateSessionToken(soloOwner.userId),
    ipAddress: '106.51.234.167',
    userAgent:
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
    deviceType: 'desktop',
    deviceName: 'Windows PC',
    browser: 'Chrome',
    browserVersion: '120',
    os: 'Windows',
    osVersion: '10',
    country: 'India',
    countryCode: 'IN',
    city: 'Pune',
    region: 'Maharashtra',
    status: 'active',
    isCurrentSession: false,
    createdAt: getPastDate(1),
    lastActivityAt: new Date().toISOString(),
    expiresAt: getExpiryDate(),
  },

  // Tablet session
  {
    sessionId: 'session-tablet-001',
    userId: mediumOwner.userId,
    sessionToken: generateSessionToken(mediumOwner.userId),
    ipAddress: '103.21.125.45',
    userAgent:
      'Mozilla/5.0 (iPad; CPU OS 17_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.1 Mobile/15E148 Safari/604.1',
    deviceType: 'tablet',
    deviceName: 'iPad Pro',
    browser: 'Safari',
    browserVersion: '17.1',
    os: 'iOS',
    osVersion: '17.1',
    country: 'India',
    countryCode: 'IN',
    city: 'Bangalore',
    region: 'Karnataka',
    status: 'active',
    isCurrentSession: false,
    createdAt: getPastDate(4),
    lastActivityAt: getPastDate(1),
    expiresAt: getExpiryDate(),
  },
]

// ============================================================================
// HELPER FUNCTIONS FOR STORE
// ============================================================================

/**
 * Get sessions for a user
 */
export function getSessionsForUser(userId: string): UserSession[] {
  return mockSessions.filter((s) => s.userId === userId)
}

/**
 * Get active sessions for a user
 */
export function getActiveSessionsForUser(userId: string): UserSession[] {
  return mockSessions.filter((s) => s.userId === userId && s.status === 'active')
}

/**
 * Get session by ID
 */
export function getSessionById(sessionId: string): UserSession | undefined {
  return mockSessions.find((s) => s.sessionId === sessionId)
}

/**
 * Get sessions by status
 */
export function getSessionsByStatus(status: SessionStatus): UserSession[] {
  return mockSessions.filter((s) => s.status === status)
}

/**
 * Get sessions by device type
 */
export function getSessionsByDeviceType(deviceType: DeviceType): UserSession[] {
  return mockSessions.filter((s) => s.deviceType === deviceType)
}

/**
 * Count active sessions for user
 */
export function countActiveSessionsForUser(userId: string): number {
  return getActiveSessionsForUser(userId).length
}

/**
 * Get all active sessions (admin view)
 */
export function getAllActiveSessions(): UserSession[] {
  return mockSessions.filter((s) => s.status === 'active')
}

/**
 * Terminate session (mock)
 */
export function terminateSession(sessionId: string): boolean {
  const session = mockSessions.find((s) => s.sessionId === sessionId)
  if (session) {
    session.status = 'revoked'
    session.loggedOutAt = new Date().toISOString()
    return true
  }
  return false
}

/**
 * Terminate all sessions for user except current
 */
export function terminateAllUserSessions(userId: string, exceptSessionId?: string): number {
  let count = 0
  mockSessions.forEach((s) => {
    if (s.userId === userId && s.status === 'active' && s.sessionId !== exceptSessionId) {
      s.status = 'revoked'
      s.loggedOutAt = new Date().toISOString()
      count++
    }
  })
  return count
}

/**
 * Create a new session (mock)
 */
export function createSession(
  userId: string,
  ipAddress: string,
  userAgent: string
): UserSession {
  const newSession: UserSession = {
    sessionId: generateSessionId(),
    userId,
    sessionToken: generateSessionToken(userId),
    ipAddress,
    userAgent,
    deviceType: 'unknown',
    browser: 'Unknown',
    os: 'Unknown',
    status: 'active',
    isCurrentSession: true,
    createdAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
    expiresAt: getExpiryDate(),
  }

  // Parse user agent (simplified)
  if (userAgent.includes('Mobile')) {
    newSession.deviceType = 'mobile'
  } else if (userAgent.includes('iPad') || userAgent.includes('Tablet')) {
    newSession.deviceType = 'tablet'
  } else {
    newSession.deviceType = 'desktop'
  }

  mockSessions.push(newSession)
  return newSession
}

// ============================================================================
// SESSION STATISTICS
// ============================================================================

/**
 * Get session statistics for admin dashboard
 */
export function getSessionStats(): {
  totalActive: number
  byDevice: Record<DeviceType, number>
  byLocation: Array<{ city: string; count: number }>
} {
  const activeSessions = getAllActiveSessions()

  const byDevice: Record<DeviceType, number> = {
    desktop: 0,
    mobile: 0,
    tablet: 0,
    unknown: 0,
  }

  const locationMap = new Map<string, number>()

  activeSessions.forEach((s) => {
    byDevice[s.deviceType]++
    if (s.city) {
      locationMap.set(s.city, (locationMap.get(s.city) || 0) + 1)
    }
  })

  const byLocation = Array.from(locationMap.entries())
    .map(([city, count]) => ({ city, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  return {
    totalActive: activeSessions.length,
    byDevice,
    byLocation,
  }
}
